import cx from "classnames"
import {
  type ChangeEvent as ReactChangeEvent,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useRef,
  useState,
} from "react"
import useBreakpoint from "../../hooks/useBreakpoint"
import {
  BinIcon,
  ContextualWarningCircleFilledIcon,
  LoadingIcon,
  PlusIcon,
} from "../Icon"
import Modal from "../Modal"
import Popover from "../Popover"
import { validateFile } from "../utils/validateFile"

const ACCEPTED_MAX_SIZE_MB = 10
const ACCEPTED_PHOTOS_TYPES = ["jpg", "jpeg", "png", "gif"]
const ERROR_DISPLAY_TIME = 6000
const ACCEPTED_TYPES_LOCALE_STRING = ACCEPTED_PHOTOS_TYPES.join(", ")

export type PhotoDropzonePropsType = {
  className?: string
  description?: string
  deleteContent: (onDelete: () => void, close: () => void) => ReactNode
  deleteContentMode?: "popover" | "modal"
  errorContent: (acceptedTypes: string, maxFileSize: number) => ReactNode
  onDropped: (file: File) => Promise<void> | void
  onPhotoDelete?: () => void
  initialImageUrl?: string
}

const preventEventDefaults = (
  e: ReactDragEvent | ReactMouseEvent | ReactKeyboardEvent | ReactTouchEvent,
) => {
  e.preventDefault?.()
  e.stopPropagation?.()
}

const isEnterOrSpaceKey = (event: ReactKeyboardEvent) =>
  event.key === "Enter" || event.key === " "

const PhotoDropzone = ({
  className,
  description,
  deleteContent,
  errorContent,
  onDropped,
  onPhotoDelete,
  deleteContentMode = "popover",
  initialImageUrl = "",
}: PhotoDropzonePropsType) => {
  const { isMobile } = useBreakpoint()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isErrored, setIsErrored] = useState(false)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(initialImageUrl)
  const [displayDeletion, setDisplayDeletion] = useState(false)
  const [showDeletePopover, setShowDeletePopover] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const openDeletePopover = (
    event?: ReactMouseEvent | ReactKeyboardEvent | ReactTouchEvent,
  ) => {
    event && preventEventDefaults(event)
    setShowDeletePopover(true)
  }
  const closeDeletePopover = (event?: ReactMouseEvent | ReactTouchEvent) => {
    event && preventEventDefaults(event)
    setShowDeletePopover(false)
  }

  const onDropzoneMouseEnter = (event?: ReactMouseEvent) => {
    event && preventEventDefaults(event)
    if (imagePreviewUrl) setDisplayDeletion(true)
  }

  const onDropzoneMouseLeave = (event?: ReactMouseEvent) => {
    event && preventEventDefaults(event)
    if (displayDeletion && !showDeletePopover) setDisplayDeletion(false)
  }

  const onDropzoneClick = (event?: ReactMouseEvent | ReactTouchEvent) => {
    event && preventEventDefaults(event)
    if (isMobile && showDeletePopover) return
    if (isLoading) return
    if (isErrored) setIsErrored(false)
    if (imagePreviewUrl) {
      if (showDeletePopover) closeDeletePopover()
      return setDisplayDeletion(!displayDeletion)
    }
    fileInputRef.current?.click()
  }

  const onDropzoneKey = (event: ReactKeyboardEvent) => {
    if (showDeletePopover) return
    if (isEnterOrSpaceKey(event)) {
      onDropzoneClick()
    }
  }

  const onDeleteKey = (event: ReactKeyboardEvent) => {
    if (isEnterOrSpaceKey(event)) {
      openDeletePopover(event)
    }
  }

  const onDelete = () => {
    if (fileInputRef.current) fileInputRef.current.value = ""
    closeDeletePopover()
    setImagePreviewUrl("")
    onPhotoDelete?.()
  }

  const onDragEnter = (event: ReactDragEvent) => {
    preventEventDefaults(event)
    if (isErrored) setIsErrored(false)
    !imagePreviewUrl && setIsDragging(true)
  }

  const onDragLeave = (event: ReactDragEvent) => {
    preventEventDefaults(event)
    setIsDragging(false)
  }

  const processFile = async (fileToProcess: File) => {
    setIsDragging(false)
    setIsErrored(false)
    setIsLoading(true)

    const isValidFile = await validateFile(
      fileToProcess,
      (fileToValidate, extension) => {
        const maxFileSize = ACCEPTED_MAX_SIZE_MB * 1000 * 1000
        const acceptedFileTypes = ACCEPTED_PHOTOS_TYPES

        if (fileToValidate.size > maxFileSize) {
          return false
        } else if (!extension || !acceptedFileTypes.includes(extension)) {
          return false
        } else {
          return true
        }
      },
    )

    if (isValidFile) {
      const imageSrc = URL.createObjectURL(fileToProcess)
      setImagePreviewUrl(imageSrc)
      await onDropped(fileToProcess)
      setIsLoading(false)
    } else {
      setIsLoading(false)
      setIsErrored(true)
      setTimeout(() => {
        setIsErrored(false)
      }, ERROR_DISPLAY_TIME)
    }
  }

  const onFileInputChanged = (event: ReactChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      processFile(event.target.files[0])
    }
  }

  const onDrop = (event: ReactDragEvent<HTMLLabelElement>) => {
    preventEventDefaults(event)
    if (event.dataTransfer?.files?.length && !imagePreviewUrl) {
      processFile(event.dataTransfer.files[0])
    }
  }

  useEffect(() => {
    if (imagePreviewUrl) setIsImageLoaded(false)
  }, [imagePreviewUrl])

  const onDeleteButtonClick = showDeletePopover
    ? closeDeletePopover
    : openDeletePopover

  return (
    <label
      className={cx("cobalt-photo-dropzone", className, {
        "cobalt-photo-dropzone--filled": imagePreviewUrl,
        "cobalt-photo-dropzone--dragging": isDragging,
        "cobalt-photo-dropzone--loading": isLoading,
        "cobalt-photo-dropzone--errored": isErrored,
        "cobalt-photo-dropzone--imageVisible": isImageLoaded,
      })}
      onMouseEnter={onDropzoneMouseEnter}
      onMouseLeave={onDropzoneMouseLeave}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onClick={onDropzoneClick}
      onKeyUp={onDropzoneKey}
      onDrop={onDrop}
      // Need to reset those listeners to avoid default browser behaviour
      onDragStart={preventEventDefaults}
      onDragEnd={preventEventDefaults}
      onDragOver={preventEventDefaults}
    >
      {isErrored && (
        <div className="cobalt-photo-dropzone__description">
          <ContextualWarningCircleFilledIcon color="error" />
          {errorContent(ACCEPTED_TYPES_LOCALE_STRING, ACCEPTED_MAX_SIZE_MB)}
        </div>
      )}
      {isLoading && (
        <div className="cobalt-photo-dropzone__description">
          <LoadingIcon />
        </div>
      )}
      {!isLoading &&
        !isErrored &&
        (imagePreviewUrl ? (
          <>
            <img
              className="cobalt-photo-dropzone__preview"
              src={imagePreviewUrl}
              alt="Uploaded preview"
              onLoad={() => {
                setIsImageLoaded(true)
              }}
            />
            <div>
              <button
                type="button"
                className={cx("cobalt-photo-dropzone__delete-button", {
                  "cobalt-photo-dropzone__delete-button--triggered":
                    displayDeletion,
                })}
                ref={deleteButtonRef}
                onClick={onDeleteButtonClick}
                onKeyUp={onDeleteKey}
              >
                <BinIcon color="onSurface" />
              </button>
              {deleteContentMode === "modal" && (
                <Modal
                  isOpen={showDeletePopover}
                  aria-label="delete"
                  bodySpacing={false}
                >
                  {deleteContent(onDelete, closeDeletePopover)}
                </Modal>
              )}

              {deleteContentMode === "popover" && (
                <Popover
                  targetRef={deleteButtonRef}
                  isOpen={showDeletePopover}
                  onOpenChange={setShowDeletePopover}
                  placement="left-start"
                  offset={[0, 12]}
                  bodySpacing={false}
                  arrow
                >
                  {deleteContent(onDelete, closeDeletePopover)}
                </Popover>
              )}
            </div>
          </>
        ) : (
          <div className="cobalt-photo-dropzone__description cobalt-photo-dropzone__description--strong">
            {description && <div>{description}</div>}
            <PlusIcon />
          </div>
        ))}
      <input
        ref={fileInputRef}
        className="cobalt-photo-dropzone__hidden-input"
        type="file"
        onChange={onFileInputChanged}
        // Prevent the bubbled click from re-entering the label's onClick,
        // which would cancel the file dialog via preventDefault.
        onClick={(event) => event.stopPropagation()}
        accept={ACCEPTED_PHOTOS_TYPES.map((ext) => `.${ext}`).join(",")}
        multiple={false}
      />
    </label>
  )
}

export default PhotoDropzone
